/**
 * Editor handbook - the one page with everything an editor needs to know
 * about the portal: first login, how cards reach them, drawing a card,
 * delivery, revisions, why offers stop, what a card pays, billing and
 * deductions, what is gone (Shawn 2026-11-01: replaces the separate Guide and
 * "How you are paid" pages, one menu entry instead of two; brought up to the
 * live portal 2026-11-02).
 *
 * Every number comes from the SAME config the server computes money and trust
 * from (`GET /api/editor/rules` -> `editor-pay-rules.ts`), never a hand-copied
 * number that can drift. Copy is translated (`../i18n/messages/handbook.ts`);
 * button names come from the catalog of the screen that shows the button.
 * If the numbers fail to load, the prose sections still render and the
 * number-bearing lines are left out, with a one-line error.
 *
 * @module web/screens/EditorHandbook
 */
import { HubPageHead } from "./EditorHub.tsx";
import { useEffect, useState } from "react";
import { get } from "../api/client.ts";
import { intlLocale } from "../i18n/core.ts";
import { useI18n } from "../i18n/index.tsx";
import "../editor.css";

interface EditorPayRules {
  stageUp: { proCards: number; veteranCards: number; revisionHours: number };
  formula: {
    formulaShare: number;
    toolCostCentsPerSecond: number;
    minimumCents: number;
    staticRateCents: number;
    legacyFlatRateCapCents: number;
    stageFactors: { novice: number; pro: number; veteran: number };
  };
  billing: { latePercent: number; lateBothPercent: number; cardLateHours: number; cardLateGraceMinutes: number; revisionLateHours: number; mentorBonusCents: number };
  revision: { warningHours: number; deductHours: number; trustDelta: number; deductEnabled: boolean };
  giveBack: { cooldownHours: number; trustCapHours: number; trustPerHour: number };
  dispatch: {
    urgentCardHours: number;
    maxOpenOffers: number;
    maxOpenOffersHighTrust: number;
    highTrustPercent: number;
    defaultWindow: { fromHour: number; untilHour: number; timezone: string };
  };
  adlingoGateThresholdPercent: number;
}

/** The editor's own sign-in address (`App.tsx#isEditorPath`) - an address, not copy. */
const EDITOR_URL = "app.aditor.ai/editor";

/** A whole Berlin hour as "08:00" - the same in both languages. */
const clock = (hour: number) => `${String(hour).padStart(2, "0")}:00`;

/** Stage names are product names, the same in every language. */
const STAGE_ROWS = [
  { name: "Newbie / Novice", stage: "novice" },
  { name: "Pro", stage: "pro" },
  { name: "Veteran", stage: "veteran" },
] as const;

export function EditorHandbookScreen({ onBack }: { onBack: () => void }) {
  const { t, eur, num, locale } = useI18n();
  // Shown as the pay increase over the base (Novice) band, never as a raw multiplier (Shawn 2026-10-31).
  const payVsBase = (f: number, base: number) => {
    const pct = Math.round((f / base - 1) * 100);
    return pct > 0 ? t("handbook.stage_increase", { pct: pct.toLocaleString(intlLocale(locale)) }) : t("handbook.stage_base");
  };
  const [rules, setRules] = useState<EditorPayRules | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    get<EditorPayRules>("/api/editor/rules")
      .then((r) => { if (live) setRules(r); })
      .catch(() => { if (live) setError(true); });
    return () => { live = false; };
  }, []);

  return (
    <div className="ed-billing hd-page" data-screen="editor-handbook">
      <HubPageHead title={t("handbook.title")} backLabel={t("handbook.back")} onBack={onBack} />
      <p className="ed-muted">{t("handbook.intro")}</p>
      {error && <p className="editor-offer-error">{t("handbook.error")}</p>}
      {!rules && !error && <p className="ed-muted">{t("handbook.loading")}</p>}
      <div className="ed-guide">
        <section>
          <h3>{t("handbook.start_title")}</h3>
          <ul>
            <li>{t("handbook.start_link", { url: EDITOR_URL })}</li>
            <li>{t("handbook.start_billing")}</li>
            <li>
              {t("handbook.start_availability")}
              {rules && <> {t("handbook.start_default_window", { from: clock(rules.dispatch.defaultWindow.fromHour), until: clock(rules.dispatch.defaultWindow.untilHour) })}</>}
            </li>
            <li>{t("handbook.start_channel", { offersVia: t("hub.offersVia") })}</li>
            <li>{t("handbook.start_language")}</li>
          </ul>
        </section>
        <section>
          <h3>{t("handbook.flow_title")}</h3>
          <p>{t("handbook.flow_body")}</p>
          <p>{t("handbook.flow_channel", { offersVia: t("hub.offersVia") })}</p>
          <p>{t("handbook.flow_answer")}</p>
        </section>
        <section>
          <h3>{t("handbook.draw_title")}</h3>
          <p>{t("handbook.draw_body", { button: t("hub.giveMeACard") })}</p>
          {rules && <p>{t("handbook.draw_what", { hours: num(rules.dispatch.urgentCardHours) })}</p>}
          <p>{t("handbook.draw_needs")}</p>
        </section>
        <section>
          <h3>{t("handbook.delivery_title")}</h3>
          <p>{t("handbook.delivery_body")}</p>
          <p>{t("handbook.delivery_checklist", { flag: t("cardDetails.flagItem") })}</p>
          <p>{t("handbook.delivery_submit")}</p>
        </section>
        <section>
          <h3>{t("handbook.revisions_title")}</h3>
          <p>{t("handbook.revisions_intro")}</p>
          <p>{t("handbook.revisions_drive")}</p>
          {rules && (
            <p>
              {t(rules.revision.deductEnabled ? "handbook.revisions_body" : "handbook.revisions_body_inactive", {
                warn: num(rules.revision.warningHours),
                deduct: num(rules.revision.deductHours),
                trust: num(Math.abs(rules.revision.trustDelta)),
              })}
            </p>
          )}
        </section>
        {rules && (
          <section>
            <h3>{t("handbook.giveback_title")}</h3>
            <p>
              {t("handbook.giveback_body", {
                cooldown: num(rules.giveBack.cooldownHours),
                from: clock(rules.dispatch.defaultWindow.fromHour),
                until: clock(rules.dispatch.defaultWindow.untilHour),
                perHour: num(rules.giveBack.trustPerHour),
                cap: num(rules.giveBack.trustCapHours),
              })}
            </p>
          </section>
        )}
        <section>
          <h3>{t("handbook.nooffers_title")}</h3>
          <p>{t("handbook.nooffers_intro")}</p>
          <ul>
            <li>{t("handbook.nooffers_hours")}</li>
            <li>{t("handbook.nooffers_revision")}</li>
            {rules && <li>{t("handbook.nooffers_pause", { cooldown: num(rules.giveBack.cooldownHours) })}</li>}
            {rules && (
              <li>
                {t("handbook.nooffers_offers", {
                  max: num(rules.dispatch.maxOpenOffers),
                  maxHigh: num(rules.dispatch.maxOpenOffersHighTrust),
                  pct: num(rules.dispatch.highTrustPercent),
                })}
              </li>
            )}
            <li>{t("handbook.nooffers_quota")}</li>
            {rules && <li>{t("handbook.nooffers_adlingo", { pct: num(rules.adlingoGateThresholdPercent) })}</li>}
          </ul>
        </section>
        <section data-handbook-fixedweek>
          <h3>{t("handbook.fixedweek_title")}</h3>
          <p>{t("handbook.fixedweek_intro")}</p>
          <ul>
            <li>{t("handbook.fixedweek_accept")}</li>
            <li>{t("handbook.fixedweek_share")}</li>
            <li>{t("handbook.fixedweek_release")}</li>
            <li>{t("handbook.fixedweek_deadline")}</li>
            <li>{t("handbook.fixedweek_nextweek")}</li>
            <li>{t("handbook.fixedweek_client")}</li>
            <li>{t("handbook.fixedweek_free")}</li>
            <li>{t("handbook.fixedweek_revision")}</li>
            <li>{t("handbook.fixedweek_team")}</li>
            <li>{t("handbook.fixedweek_messages")}</li>
          </ul>
        </section>
        <section>
          <h3>{t("handbook.pricing_title")}</h3>
          <p>{t("handbook.pricing_body")}</p>
          {rules && (
            <>
              <ul>
                <li>{t("handbook.rate_share", { pct: num(Math.round(rules.formula.formulaShare * 100)) })}</li>
                <li>{t("handbook.rate_tool_cost", { amount: eur(rules.formula.toolCostCentsPerSecond) })}</li>
                <li>{t("handbook.rate_minimum", { amount: eur(rules.formula.minimumCents) })}</li>
                <li>{t("handbook.rate_static", { amount: eur(rules.formula.staticRateCents) })}</li>
                <li>{t("handbook.rate_legacy_cap", { amount: eur(rules.formula.legacyFlatRateCapCents) })}</li>
              </ul>
              <h3>{t("handbook.stage_title")}</h3>
              <ul>
                {STAGE_ROWS.map((r) => (
                  <li key={r.stage}>{r.name}: {payVsBase(rules.formula.stageFactors[r.stage], rules.formula.stageFactors.novice)}</li>
                ))}
              </ul>
              <ul>
                <li>{t("handbook.stage_up_novice", { adlingo: rules.adlingoGateThresholdPercent })}</li>
                <li>{t("handbook.stage_up_pro", { cards: rules.stageUp.proCards })}</li>
                <li>{t("handbook.stage_up_veteran", { cards: rules.stageUp.veteranCards })}</li>
              </ul>
              <p>{t("handbook.stage_up_revisions", { hours: rules.stageUp.revisionHours })}</p>
              <p className="ed-muted">{t("handbook.stage_locked")}</p>
            </>
          )}
        </section>
        <section>
          <h3>{t("handbook.billing_title")}</h3>
          <p>{t("handbook.billing_body")}</p>
          <ul>
            <li>{t("handbook.billing_claims")}</li>
            <li>{t("handbook.billing_sent", { markSent: t("billing.mark_sent") })}</li>
            <li>{t("handbook.billing_messages")}</li>
            {rules && <li>{t("handbook.billing_late", { pct: rules.billing.latePercent, both: rules.billing.lateBothPercent, cardHours: rules.billing.cardLateHours, graceMinutes: rules.billing.cardLateGraceMinutes, revisionHours: rules.billing.revisionLateHours })}</li>}
            {rules && <li>{t("handbook.billing_mentor", { amount: eur(rules.billing.mentorBonusCents) })}</li>}
            <li>{t("handbook.billing_gen")}</li>
          </ul>
        </section>
        <section>
          <h3>{t("handbook.trust_title")}</h3>
          <p>{t("handbook.trust_body")}</p>
        </section>
        <section>
          {/* "AdLingo" is the product's name, not copy. */}
          <h3>AdLingo</h3>
          <p>{t("handbook.adlingo_body")}</p>
          {rules && <p>{t("handbook.adlingo_gate", { pct: num(rules.adlingoGateThresholdPercent) })}</p>}
        </section>
        <section>
          <h3>{t("handbook.gone_title")}</h3>
          <ul>
            <li>{t("handbook.gone_trello")}</li>
            <li>{t("handbook.gone_appeals")}</li>
            <li>{t("handbook.gone_email")}</li>
            <li>{t("handbook.gone_slots")}</li>
          </ul>
        </section>
        <section>
          <h3>{t("handbook.help_title")}</h3>
          <p>{t("handbook.help_body")}</p>
        </section>
      </div>
    </div>
  );
}
